import React from 'react';
import { MessageSquare, Lightbulb, Hammer, CheckCircle } from 'lucide-react';

const Process = () => {
  const steps = [
    {
      icon: MessageSquare,
      title: 'Consultation',
      description: 'During the initial consultation phase, we discuss your requirements and vision for your design project. This is where we ensure we are a good fit for your project and understand your goals.'
    },
    {
      icon: Lightbulb,
      title: 'Conceptualization',
      description: 'We identify your desires as well as the regulations, budget, and geographical and cultural context that determines the scope, potential, and parameters of your project.'
    },
    {
      icon: Hammer,
      title: 'Development',
      description: 'The process of synchronizing the design with documents from the various consultants involved. The design is further elaborated to assemble all details and specifications needed for construction documents.'
    },
    {
      icon: CheckCircle,
      title: 'Execution & Delivery',
      description: 'Representing you on site, setting the project\'s construction pace and progress. We remain available for consultation to the General Contractor and coordinate consultants as needed for site intervention.'
    }
  ];

  return (
    <section id="process" className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-30 -z-0"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-30 -z-0"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-4">
            Our <span className="text-yellow-600">Process</span>
          </h2>
          <div className="w-24 h-1 bg-yellow-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            We follow a systematic four-step process to ensure your project is delivered with excellence from concept to completion.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-1 bg-yellow-200" style={{ width: '85%', margin: '0 auto' }}></div>

          <div className="grid lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Step Card */}
                  <div className="bg-white rounded-sm p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full">
                    {/* Step Number & Icon */}
                    <div className="flex flex-col items-center mb-6">
                      <div className="relative mb-4">
                        <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center shadow-lg relative z-10">
                          <Icon className="w-10 h-10 text-white" />
                        </div>
                        {/* Step Number */}
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-black mb-4 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-center">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-xl text-gray-700 mb-6">
            Ready to bring your vision to life?
          </p>
          <button
            onClick={() => {
              const element = document.querySelector('#contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 bg-yellow-600 text-white font-semibold rounded-sm hover:bg-yellow-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            Start Your Project
          </button>
        </div>
      </div>
    </section>
  );
};

export default Process;